import { useState, useEffect, useCallback } from 'react';
import {
  DashboardResponse,
  CheckInDashboardData,
} from '../../../types/dashboard';
import { ScanLine, UserCheck, Clock } from 'lucide-react';
import { apiClient } from '../../../api/client';

interface CheckInPanelProps {
  data: DashboardResponse;
}

interface QueueItem {
  id: string;
  queueName: string;
  guestName: string;
  ticketNumber: string | null;
  orderNumber: string | null;
  location: string;
  createdAt: string;
}

const styles = {
  container: {
    padding: 'clamp(16px, 4vw, 48px)',
    overflow: 'hidden' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
    background: '#0d1b12',
    color: '#ffffff',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(12px, 3vw, 32px)',
  },
  heading: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    color: '#c9a84c',
    margin: 0,
    fontWeight: 700,
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    alignItems: 'flex-end',
  },
  statBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  statNumber: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#ffffff',
    margin: 0,
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#e0e0e0',
    margin: '8px 0 0 0',
  },
  rateNumber: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#1a6b3c',
    margin: 0,
    lineHeight: 1.1,
  },
  sectionTitle: {
    fontSize: 'clamp(16px, 2.5vw, 28px)',
    color: '#c9a84c',
    margin: '0 0 12px 0',
    fontWeight: 600,
  },
  recentList: {
    flex: 1,
    overflow: 'hidden' as const,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
    fontSize: 'clamp(9px, 1.2vw, 14px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  listItemName: {
    color: '#ffffff',
  },
  listItemChapter: {
    color: '#e0e0e0',
    fontSize: 'clamp(12px, 1.7vw, 20px)',
  },
  listItemTime: {
    color: '#c9a84c',
    fontSize: 'clamp(12px, 1.7vw, 20px)',
  },
};

/**
 * Check-in area panel — shows check-in progress (total/registered),
 * rate percentage, and recent check-ins list.
 */
export function CheckInPanel({ data }: CheckInPanelProps) {
  const checkInData = data as CheckInDashboardData;
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);

  const fetchQueue = useCallback(async () => {
    try {
      const items = await apiClient<QueueItem[]>('/public/dashboard/delivery-queue');
      setQueueItems(Array.isArray(items) ? items : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 10_000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  return (
    <div style={{ ...styles.container, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'clamp(12px, 2vw, 24px)' }}>
      {/* Left: Check-In Stats + Recent */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 3vw, 32px)' }}>
        <h1 style={styles.heading}>
          <ScanLine size={42} style={{ marginRight: 16, verticalAlign: 'middle' }} />
          Check-In
        </h1>

        <div style={styles.statsRow}>
          <div style={styles.statBlock}>
            <p style={styles.statNumber}>
              {checkInData.totalCheckedIn} / {checkInData.totalRegistered}
            </p>
            <p style={styles.statLabel}>Checked In / Registered</p>
          </div>

          <div style={styles.statBlock}>
            <p style={styles.rateNumber}>{checkInData.checkInRate}%</p>
            <p style={styles.statLabel}>Check-In Rate</p>
          </div>
        </div>

        <div style={styles.recentList}>
          <h2 style={styles.sectionTitle}>Recent Check-Ins</h2>
          {checkInData.recentCheckIns.map((item, index) => (
            <div key={index} style={styles.listItem}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <UserCheck size={20} color="#1a6b3c" style={{ marginRight: 10, flexShrink: 0 }} />
                <span style={styles.listItemName}>{item.name}</span>
                <span style={styles.listItemChapter}> — {item.chapter}</span>
              </div>
              <span style={{ ...styles.listItemTime, display: 'flex', alignItems: 'center' }}>
                <Clock size={16} color="#c9a84c" style={{ marginRight: 6 }} />
                {formatTime(item.checkedInAt)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Delivery Queue */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 'clamp(16px, 3vw, 40px)' }}>
        <h2 style={{ ...styles.sectionTitle, display: 'flex', alignItems: 'center', gap: '10px' }}>
          📦 Delivery Queue
          <span style={{ fontSize: 'clamp(14px, 2vw, 22px)', background: '#c9a84c', color: '#0d1b12', padding: '2px 10px', borderRadius: '12px', fontWeight: 700 }}>
            {queueItems.length}
          </span>
        </h2>
        <div style={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: '1fr 2px 1fr', gap: '12px' }}>
          {['Deep Wata Quay', 'Govna Wharf'].map((qName, qIdx) => {
            const items = queueItems.filter((i) => i.queueName === qName || (!i.queueName && qName === 'Deep Wata Quay'));
            return (
              <>{qIdx === 1 && <div style={{ background: 'rgba(201,168,76,0.4)', width: '2px' }} />}<div key={qName}>
                <div style={{ fontSize: 'clamp(12px, 1.5vw, 18px)', fontWeight: 700, color: '#c9a84c', marginBottom: '8px', borderBottom: '1px solid rgba(201,168,76,0.3)', paddingBottom: '4px' }}>
                  🚢 {qName} <span style={{ color: '#888', fontWeight: 400 }}>({items.length})</span>
                </div>
                {items.length === 0 ? (
                  <p style={{ color: '#555', fontSize: 'clamp(12px, 1.5vw, 16px)' }}>Empty</p>
                ) : (
                  <>
                  {items.slice(0, 8).map((item, idx) => {
                    const positionTag = idx === 0 ? { icon: '🔥', label: 'Naw naw!' }
                      : idx === 1 ? { icon: '🚶', label: 'Kam ya' }
                      : idx === 2 ? { icon: '⏳', label: 'Smol tam' }
                      : { icon: '🌴', label: 'Hol ya' };
                    return (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 'clamp(13px, 1.8vw, 20px)' }}>
                        <span style={{ fontSize: '1.3em' }}>{positionTag.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: '#fff' }}>{item.guestName}</span>
                          <div style={{ fontSize: '0.75em', color: '#c9a84c', marginTop: '2px' }}>
                            {item.ticketNumber && <span>{item.ticketNumber}</span>}
                            {item.ticketNumber && item.orderNumber && <span> · </span>}
                            {item.orderNumber && <span>📋 {item.orderNumber}</span>}
                            {!item.ticketNumber && !item.orderNumber && <span style={{ color: '#666' }}>{positionTag.label}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {items.length > 8 && (
                    <div style={{ padding: '8px 0', fontSize: 'clamp(12px, 1.5vw, 18px)', color: '#c9a84c', fontWeight: 600, textAlign: 'center' }}>
                      +{items.length - 8} more waiting...
                    </div>
                  )}
                  </>
                )}
              </div></>
            );
          })}
        </div>
      </div>
    </div>
  );
}
